import { btnPrimary, btnSecondary } from '@/components/record-sheet/styles'
import type { WorkflowDetail, WorkflowRun } from '@/lib/workflows'
import { RUN_ACTIVE } from '@/lib/workflows'
import { cn } from '@/lib/utils'
import {
  AlertCircle,
  CheckCircle2,
  ArrowUp,
  CircleStop,
  Loader2,
  MessagesSquare,
  Play
} from 'lucide-react'
import { type KeyboardEvent, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { AgentAvatar, ChiefOrb } from '../AgentAvatar'
import { BuildQuestions } from './BuildQuestions'
import { RUN_TONE, TYPE_META, timeAgo } from './shared'

// --- Build ---------------------------------------------------------------------------------

export function BuildPanel({
  wf,
  dirty,
  onRefine,
  onStop,
  onShowNode,
  composer = true
}: {
  wf: WorkflowDetail
  dirty: boolean
  /** False inside the chat: the chat's own message box does the asking there. */
  composer?: boolean
  onRefine: (prompt: string) => Promise<void>
  onStop: () => Promise<void>
  onShowNode: (id: string) => void
}) {
  const [stopping, setStopping] = useState(false)
  const building = wf.build_status === 'building'
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const end = useRef<HTMLDivElement>(null)
  const lines = wf.build_log
  // Finished: not building or asking, the team drew something, and the log ends with its sign-off.
  const done = wf.build_status === 'idle' && wf.graph.nodes.length > 0 && lines.length > 0
  const steps = wf.graph.nodes.filter((n) => n.type !== 'trigger').length
  const last = lines[lines.length - 1]
  const finishedAt = last
    ? new Date(last.at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : ''

  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end', behavior: 'smooth' })
  }, [lines.length])

  async function send() {
    const prompt = text.trim()
    if (!prompt || sending || building || dirty) return
    setSending(true)
    try {
      await onRefine(prompt)
      setText('')
    } finally {
      setSending(false)
    }
  }

  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send()
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 space-y-4 overflow-y-auto px-4 pb-4">
        {building && (
          <div className="flex items-center gap-3 rounded-[24px] bg-neutral-100 p-4 dark:bg-neutral-900">
            <ChiefOrb size="lg" state="working" />
            <div className="min-w-0 flex-1">
              <p className="font-bold text-neutral-900 dark:text-white">The team is building it</p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                Watch the canvas: each step appears as it's added.
              </p>
            </div>
            <button
              type="button"
              className={cn(btnSecondary, 'shrink-0 px-3 py-1.5 text-xs')}
              disabled={stopping}
              onClick={async () => {
                setStopping(true)
                try {
                  await onStop()
                } finally {
                  setStopping(false)
                }
              }}
            >
              {stopping ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <CircleStop className="size-3.5" />
              )}
              Stop
            </button>
          </div>
        )}

        {done && (
          <div className="flex items-start gap-3 rounded-[24px] bg-emerald-50 p-4 dark:bg-emerald-500/10">
            <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
            <div className="min-w-0">
              <p className="font-bold text-emerald-800 dark:text-emerald-300">Done building</p>
              <p className="mt-0.5 text-sm text-emerald-800/80 dark:text-emerald-300/80">
                {steps} step{steps === 1 ? '' : 's'}
                {finishedAt && ` · finished ${finishedAt}`}.{' '}
                {wf.errors.length > 0
                  ? 'A few details to fill in below.'
                  : wf.enabled
                    ? "It's on."
                    : 'Press Run now to try it, or switch it on at the top.'}
              </p>
            </div>
          </div>
        )}

        {wf.prompt && (
          <div className="rounded-[24px] bg-neutral-100 p-4 dark:bg-neutral-900">
            <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              You asked
            </p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-neutral-800 dark:text-neutral-200">
              {wf.prompt}
            </p>
          </div>
        )}

        {wf.build_status === 'failed' && wf.build_error && (
          <div className="flex gap-2 rounded-[24px] bg-red-50 p-4 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-400">
            <AlertCircle className="mt-0.5 size-4 shrink-0" />
            {wf.build_error}
          </div>
        )}

        {wf.build_status === 'asking' && (
          <div className="space-y-3 rounded-[24px] bg-neutral-100 p-4 dark:bg-neutral-900">
            <p className="font-bold text-neutral-900 dark:text-white">
              Chief of Staff has a few questions first
            </p>
            <BuildQuestions wf={wf} />
          </div>
        )}
        {!building && wf.errors.length > 0 && wf.graph.nodes.length > 0 && (
          <div className="rounded-[24px] bg-amber-50 p-4 dark:bg-amber-500/10">
            <p className="text-sm font-bold text-amber-800 dark:text-amber-400">
              Fill these in before switching it on
            </p>
            <ul className="mt-2 space-y-1 text-sm text-amber-800 dark:text-amber-300">
              {wf.errors.map((e) => (
                <li key={e}>• {e}</li>
              ))}
            </ul>
          </div>
        )}

        {lines.length > 0 && (
          <div>
            <p className="mb-2 px-1 text-[11px] font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              Build log
            </p>
            <ol className="space-y-2.5">
              {lines.map((line, i) => (
                <li
                  key={`${line.at}-${i}`}
                  className="flex items-start gap-2.5 animate-in fade-in-0 slide-in-from-bottom-1 duration-300"
                >
                  {done && i === lines.length - 1 ? (
                    <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-emerald-500" />
                  ) : (
                    <AgentAvatar
                      agentKey={line.agent}
                      size="xs"
                      state={building && i === lines.length - 1 ? 'working' : 'idle'}
                      className="mt-0.5"
                    />
                  )}
                  <span className="min-w-0 flex-1 text-sm text-neutral-800 dark:text-neutral-200">
                    {line.node && wf.graph.nodes.some((n) => n.id === line.node) ? (
                      <button
                        type="button"
                        onClick={() => onShowNode(line.node as string)}
                        className="text-left underline-offset-4 hover:underline"
                      >
                        {line.text}
                      </button>
                    ) : (
                      line.text
                    )}
                    {building && i === lines.length - 1 && (
                      <Loader2 className="ml-1.5 inline size-3 animate-spin text-neutral-400" />
                    )}
                  </span>
                  <time className="shrink-0 text-[11px] tabular-nums text-neutral-400">
                    {new Date(line.at).toLocaleTimeString(undefined, {
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                  </time>
                </li>
              ))}
            </ol>
            <div ref={end} />
          </div>
        )}
      </div>

      {composer && (
      // Right margin keeps the send button clear of the floating assistant button.
      <div className="border-t border-neutral-200 p-3 pr-16 dark:border-neutral-800">
        {dirty && (
          <p className="mb-2 px-2 text-[11px] text-amber-700 dark:text-amber-400">
            Save your changes first, then ask the team for more.
          </p>
        )}
        <div className="flex items-end gap-2 rounded-[24px] border border-neutral-200 bg-white py-1.5 pl-4 pr-1.5 focus-within:border-neutral-400 dark:border-neutral-800 dark:bg-neutral-900 dark:focus-within:border-neutral-600">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKey}
            rows={Math.min(5, Math.max(1, text.split('\n').length))}
            disabled={building || dirty}
            placeholder={building ? 'The team is building…' : 'Ask the team to change it…'}
            aria-label="Ask the team to change the workflow"
            className="max-h-32 flex-1 resize-none bg-transparent py-1.5 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none disabled:opacity-60 dark:text-white"
          />
          <button
            type="button"
            onClick={send}
            disabled={!text.trim() || sending || building || dirty}
            aria-label="Send"
            className="flex size-8 shrink-0 items-center justify-center rounded-full bg-neutral-900 text-white transition-all active:scale-95 disabled:opacity-40 dark:bg-white dark:text-black"
          >
            {sending ? <Loader2 className="size-4 animate-spin" /> : <ArrowUp className="size-4" />}
          </button>
        </div>
      </div>
      )}
    </div>
  )
}

// --- Runs ------------------------------------------------------------------------------------

export function RunsPanel({
  wf,
  run,
  selected,
  canRun,
  runHint,
  starting,
  onRun,
  onSelect,
  onCancel
}: {
  wf: WorkflowDetail
  run: WorkflowRun | undefined
  selected: number | null
  canRun: boolean
  runHint: string
  starting: boolean
  onRun: () => void
  onSelect: (id: number | null) => void
  onCancel: (id: number) => void
}) {
  return (
    <div className="flex h-full flex-col">
      <div className="space-y-2 px-4 pb-3">
        <button
          type="button"
          className={cn(btnPrimary, 'w-full justify-center')}
          disabled={!canRun || starting}
          onClick={onRun}
        >
          {starting ? <Loader2 className="size-4 animate-spin" /> : <Play className="size-4" />}
          Run now
        </button>
        {runHint && <p className="px-2 text-center text-[11px] text-neutral-400">{runHint}</p>}
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto px-4 pb-4">
        {wf.runs.length === 0 && (
          <div className="rounded-[24px] bg-neutral-100 p-5 text-center dark:bg-neutral-900">
            <p className="text-sm font-semibold text-neutral-900 dark:text-white">No runs yet</p>
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              Press Run now to try it. Every step shows up on the canvas as it goes.
            </p>
          </div>
        )}

        {wf.runs.length > 0 && (
          <ul className="space-y-1">
            {wf.runs.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => onSelect(selected === r.id ? null : r.id)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-[24px] px-3 py-2 text-left transition-colors',
                    selected === r.id
                      ? 'bg-neutral-100 dark:bg-neutral-900'
                      : 'hover:bg-neutral-50 dark:hover:bg-neutral-900/60'
                  )}
                >
                  <span
                    className={cn(
                      'shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold',
                      RUN_TONE[r.status]
                    )}
                  >
                    {r.status_label}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-xs text-neutral-600 dark:text-neutral-300">
                    {r.trigger_text}
                  </span>
                  <span className="shrink-0 text-[11px] text-neutral-400">{timeAgo(r.started_at)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {selected && run && run.id === selected && <RunDetail run={run} onCancel={onCancel} />}
        {selected && (!run || run.id !== selected) && (
          <div className="h-32 animate-pulse rounded-[24px] bg-neutral-100 dark:bg-neutral-900" />
        )}
      </div>
    </div>
  )
}

function RunDetail({ run, onCancel }: { run: WorkflowRun; onCancel: (id: number) => void }) {
  const active = RUN_ACTIVE.includes(run.status)
  const waitingCard = run.steps?.some((s) => s.status === 'waiting' && s.card)
  return (
    <div className="space-y-3 rounded-[24px] bg-neutral-100 p-4 dark:bg-neutral-900">
      <div className="flex items-center gap-2">
        <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-semibold', RUN_TONE[run.status])}>
          {run.status_label}
        </span>
        <span className="text-xs text-neutral-500 dark:text-neutral-400">
          {new Date(run.started_at).toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
          })}
          {run.started_by && ` · @${run.started_by}`}
        </span>
        {active && (
          <button
            type="button"
            className={cn(btnSecondary, 'ml-auto px-3 py-1 text-xs')}
            onClick={() => onCancel(run.id)}
          >
            <CircleStop className="size-3.5" /> Stop
          </button>
        )}
      </div>
      {run.error && (
        <p className="rounded-[24px] bg-red-50 p-3 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-400">
          {run.error}
        </p>
      )}
      {waitingCard && (
        <Link
          to="/apps/ai-team/chat"
          className="flex items-center gap-2 rounded-[24px] bg-amber-50 p-3 text-sm font-semibold text-amber-800 transition-colors hover:bg-amber-100 dark:bg-amber-500/10 dark:text-amber-300 dark:hover:bg-amber-500/20"
        >
          <MessagesSquare className="size-4 shrink-0" />
          Waiting for your decision in the Team room
        </Link>
      )}
      <ol className="space-y-2">
        {run.steps?.map((s) => {
          const Icon = TYPE_META[s.type]?.icon
          return (
            <li key={s.id} className="rounded-[24px] bg-white p-3 dark:bg-black">
              <div className="flex items-center gap-2">
                {Icon && <Icon className="size-3.5 shrink-0 text-neutral-400" />}
                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-neutral-900 dark:text-white">
                  {s.label}
                </span>
                <span className="shrink-0 text-[11px] font-semibold text-neutral-500 dark:text-neutral-400">
                  {s.branch === 'error'
                    ? 'Failed · took the error path'
                    : s.branch
                      ? `${s.status_label} · ${s.branch}`
                      : s.status_label}
                </span>
              </div>
              {s.output && (
                <p className="mt-1.5 line-clamp-6 whitespace-pre-wrap break-words text-xs leading-relaxed text-neutral-600 dark:text-neutral-300">
                  {s.output}
                </p>
              )}
              {s.status === 'waiting' && s.resume_at && (
                <p className="mt-1.5 text-xs text-amber-700 dark:text-amber-400">
                  {s.error
                    ? `Trying again (attempt ${s.attempts + 1}) at ${new Date(s.resume_at).toLocaleTimeString()}`
                    : `Carries on ${new Date(s.resume_at).toLocaleString()}`}
                </p>
              )}
              {s.card && s.status === 'waiting' && (
                <p className="mt-1.5 text-xs text-amber-700 dark:text-amber-400">Card: {s.card.title}</p>
              )}
              {s.error && <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">{s.error}</p>}
            </li>
          )
        })}
      </ol>
    </div>
  )
}

